import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "space-y-3" }
const _hoisted_2 = { class: "text-sm font-medium text-gray-700" }
const _hoisted_3 = { class: "relative" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 0,
  class: "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
}
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "flex items-center justify-between" }
const _hoisted_8 = { class: "flex items-center gap-2" }
const _hoisted_9 = { class: "text-xs font-mono text-gray-400" }
const _hoisted_10 = { class: "text-sm font-medium text-gray-900" }
const _hoisted_11 = { class: "text-xs text-gray-500" }
const _hoisted_12 = {
  key: 0,
  class: "text-xs text-gray-500 ml-14"
}
const _hoisted_13 = {
  key: 1,
  class: "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3"
}
const _hoisted_14 = {
  key: 2,
  class: "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3"
}
const _hoisted_15 = {
  key: 0,
  class: "space-y-2"
}
const _hoisted_16 = { class: "flex-1" }
const _hoisted_17 = { class: "flex items-center gap-2" }
const _hoisted_18 = { class: "text-xs font-mono text-gray-400" }
const _hoisted_19 = { class: "text-sm font-medium text-gray-700" }
const _hoisted_20 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_21 = ["onClick"]
const _hoisted_22 = {
  key: 1,
  class: "text-sm text-gray-500"
}

import { ref, watch } from 'vue'
import type { Produit } from '@/types/produit'
import { searchProductsByReference } from '@/api/produits'
import { useAuthStore } from '@/stores/auth'

interface Props {
  modelValue: Produit[]
  label?: string
  placeholder?: string
  emptyMessage?: string
}

interface Emits {
  (e: 'update:modelValue', value: Produit[]): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductAutocomplete',
  props: {
    modelValue: {},
    label: { default: 'Référence(s) à associer :' },
    placeholder: { default: 'Rechercher un produit par référence ou ID...' },
    emptyMessage: { default: 'Aucun produit associé' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const authStore = useAuthStore()
const searchTerm = ref('')
const suggestions = ref<Produit[]>([])
const showSuggestions = ref(false)
const isSearching = ref(false)
let searchTimeout: ReturnType<typeof setTimeout> | null = null

// Recherche de produits avec debounce
const handleSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  
  const term = searchTerm.value.trim()
  
  if (term.length < 2) {
    suggestions.value = []
    return
  }
  
  isSearching.value = true
  
  searchTimeout = setTimeout(async () => {
    try {
      const vendeurId = authStore.user?.id_vendeur
      if (!vendeurId) {
        return
      }
      
      const results = await searchProductsByReference(vendeurId, term, 20)
      // Filtrer les produits déjà sélectionnés
      suggestions.value = results.filter(
        p => !props.modelValue.some(sp => sp.id_produit === p.id_produit)
      )
    } catch (error) {
      // Erreur silencieuse - les produits restent vides
    } finally {
      isSearching.value = false
    }
  }, 300)
}

// Sélectionner un produit
const selectProduct = (product: Produit) => {
  if (!props.modelValue.some(p => p.id_produit === product.id_produit)) {
    emit('update:modelValue', [...props.modelValue, product])
  }
  searchTerm.value = ''
  suggestions.value = []
  showSuggestions.value = false
}

// Retirer un produit
const removeProduct = (idProduit: number) => {
  emit('update:modelValue', props.modelValue.filter(p => p.id_produit !== idProduit))
}

// Gérer le blur de l'input
const handleBlur = () => {
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Réinitialiser les suggestions quand modelValue change
watch(() => props.modelValue, () => {
  if (searchTerm.value) {
    const term = searchTerm.value.trim()
    if (term.length >= 2) {
      suggestions.value = suggestions.value.filter(
        p => !props.modelValue.some(sp => sp.id_produit === p.id_produit)
      )
    }
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("label", _hoisted_2, _toDisplayString(_ctx.label), 1),
    _createElementVNode("div", _hoisted_3, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
        type: "text",
        placeholder: _ctx.placeholder,
        onInput: handleSearch,
        onFocus: _cache[1] || (_cache[1] = ($event: any) => (showSuggestions.value = true)),
        onBlur: handleBlur,
        class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      }, null, 40, _hoisted_4), [
        [
          _vModelText,
          searchTerm.value,
          void 0,
          { trim: true }
        ]
      ]),
      (showSuggestions.value && suggestions.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestions.value, (product) => {
              return (_openBlock(), _createElementBlock("div", {
                key: product.id_produit,
                onClick: ($event: any) => (selectProduct(product)),
                class: "px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              }, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("span", _hoisted_9, "#" + _toDisplayString(product.id_produit), 1),
                    _createElementVNode("span", _hoisted_10, _toDisplayString(product.reference), 1)
                  ]),
                  _createElementVNode("span", _hoisted_11, _toDisplayString(product.prix) + "€", 1)
                ]),
                (product.reference_base)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, " Réf. base: " + _toDisplayString(product.reference_base), 1))
                  : _createCommentVNode("", true)
              ], 8, _hoisted_6))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (showSuggestions.value && searchTerm.value.length >= 2 && suggestions.value.length === 0 && !isSearching.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, _cache[2] || (_cache[2] = [
            _createElementVNode("p", { class: "text-sm text-gray-500" }, "Aucun produit trouvé", -1)
          ])))
        : _createCommentVNode("", true),
      (isSearching.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_14, _cache[3] || (_cache[3] = [
            _createElementVNode("p", { class: "text-sm text-gray-500" }, [
              _createElementVNode("i", { class: "fas fa-spinner fa-spin mr-2" }),
              _createTextVNode(" Recherche en cours... ")
            ], -1)
          ])))
        : _createCommentVNode("", true)
    ]),
    (_ctx.modelValue.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.modelValue, (product) => {
            return (_openBlock(), _createElementBlock("div", {
              key: product.id_produit,
              class: "flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md"
            }, [
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("span", _hoisted_18, "#" + _toDisplayString(product.id_produit), 1),
                  _createElementVNode("span", _hoisted_19, _toDisplayString(product.reference), 1),
                  (product.prix)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_20, "(" + _toDisplayString(product.prix) + "€)", 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("button", {
                type: "button",
                onClick: ($event: any) => (removeProduct(product.id_produit)),
                class: "text-red-600 hover:text-red-800"
              }, _cache[4] || (_cache[4] = [
                _createElementVNode("i", { class: "fas fa-times" }, null, -1)
              ]), 8, _hoisted_21)
            ]))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_22, _toDisplayString(_ctx.emptyMessage), 1))
  ]))
}
}

})